/**
 * useAISummary - Shared hook for AI summary generation
 *
 * Extracts common SSE generation logic used by PlayerSummaryCard and AIPlayerSummary.
 * Handles language selection, caching, error handling, and content truncation.
 * Persists the last-generated language per player to AsyncStorage so the
 * correct cached summary survives component remounts across tab navigation (SE-4074).
 * @module features/player-detail/hooks/useAISummary
 */

import * as Sentry from "@sentry/react-native";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  CACHE_KEY_PREFIX,
  SCOUTING_CACHE_KEY_PREFIX,
  cleanSummaryOutput,
  formatLastUpdated,
  generatePlayerPrompt,
  generateScoutingSummaryPrompt,
  loadCachedSummary,
  loadLastGeneratedLanguage,
  saveLastGeneratedLanguage,
  saveSummaryToCache,
  type SummaryType,
} from "../utils";

import {
  SUPPORTED_UI_LOCALE_OPTIONS,
  getLanguageLabel,
  isSupportedUiLocale,
  type LocaleOption,
} from "@/constants/locales";
import type { LanguageSelectorSaveDefault } from "@/components/molecules/LanguageSelector";
import { useAuth, useCurrentUser } from "@/features/auth";
import { buildPlayerSummaryRequest } from "@/features/knowledge-graph/utils/authUtils";
/*
 * `SupportedLocale`, the query type and the fragment are imported as TYPES on
 * purpose (SE-6505): consumers of this module — including
 * `AIPlayerSummaryContainer`'s suite — mock `@/generated/graphql` wholesale, so
 * a value import of the enum would blow up at module scope in every mock that
 * forgot to carry it. `useAuthUpdateMyProfileMutation` is the one genuine
 * runtime import, and every such mock already provides it.
 */
import {
  type NotesListNotesByPlayerIdQuery,
  type PlayerDetailPlayerMetaFragment,
  type SupportedLocale,
  useAuthUpdateMyProfileMutation,
} from "@/generated/graphql";
import { env } from "@/lib/env";
import {
  recordSessionGeneratedSummary,
  resolveSessionGeneratedSummary,
  sessionGeneratedSummariesVar,
} from "@/stores/aiSummarySession";

import { GenerationMetadata, useSSEGeneration } from "./useSSEGeneration";

/** Default truncation limit for summary display */
const DEFAULT_TRUNCATION_LIMIT = 500;

/** Used when neither the user nor the profile has named a language. */
const FALLBACK_LANGUAGE = "en_US";

/**
 * Catalog key for the copy surfaced when the explicit save-as-default write
 * does not land (SE-6512). The copy itself lives in `locales/*.json`.
 */
export const SAVE_DEFAULT_LANGUAGE_ERROR_KEY = "language.saveDefault.failed";

/**
 * The state of the "save as my default language" affordance (SE-6511).
 *
 * A discriminated union rather than a pair of booleans on purpose: the whole
 * point of this ticket is that an override can be SET, ABSENT (inheriting the
 * default) or NOT YET RESOLVED, and a boolean cannot hold three answers. Every
 * consumer switches exhaustively, so adding a member breaks compilation at each
 * switch instead of silently falling through to "looks saved".
 */
export type SaveDefaultLanguageState =
  /** No override is set, so there is nothing to save. */
  | { readonly status: "unavailable" }
  /** An override is set and differs from the stored default. */
  | { readonly status: "idle" }
  /** The profile write is in flight. */
  | { readonly status: "saving" }
  /** The profile write landed. The confirmation the AC asks for. */
  | { readonly status: "saved" }
  /**
   * An override is set and already equals the stored default — distinct from
   * `saved`, which claims THIS surface just wrote it, and from `idle`, which
   * offers a write that would be a no-op.
   */
  | { readonly status: "already-default" }
  /** The write failed. Never reported as a success. */
  | { readonly status: "error"; readonly message: string };

/**
 * The lifecycle of the one explicit profile write, kept separate from the
 * derived {@link SaveDefaultLanguageState} so the reducer above stays a pure
 * function of (override, profile, outcome).
 */
type SaveOutcome = "none" | "saving" | "saved" | "error";

/**
 * Projects {@link SaveDefaultLanguageState} onto the picker's prop.
 *
 * `unavailable` maps to `undefined` — the row is ABSENT, not present in some
 * neutral disguise. With no override there is nothing to promote, and a row
 * offering to save the value the user is already inheriting would invent a
 * choice they have not made.
 *
 * The switch is exhaustive on purpose. Adding a state to the union without
 * deciding how the picker renders it should stop the build, not fall through to
 * whatever the last branch happened to return.
 * @param state - The state the hook derived
 * @param onPress - The hook's save action
 * @returns The picker prop, or undefined when there is nothing to offer
 */
export function toSaveDefaultProp(
  state: SaveDefaultLanguageState,
  onPress: () => void
): LanguageSelectorSaveDefault | undefined {
  switch (state.status) {
    case "unavailable":
      return undefined;
    case "error":
      return { status: "error", onPress, errorMessage: state.message };
    case "idle":
    case "saving":
    case "saved":
    case "already-default":
      return { status: state.status, onPress };
  }
}

/**
 * What {@link useAISummaryLanguage} hands a summary surface.
 */
interface UseAISummaryLanguageReturn {
  /**
   * The language generation will actually run in. Always names a language,
   * because a generation cannot be dispatched without one — so it is NOT the
   * value a picker should mark. Use {@link pickerLocale} for that.
   */
  readonly selectedLanguage: string;
  /**
   * The selection as a PICKER should read it: `undefined` when nothing has
   * resolved yet.
   *
   * Separate from {@link selectedLanguage} because the two answer different
   * questions. `selectedLanguage` answers "what will be generated", and must
   * always have an answer. `pickerLocale` answers "what has this user chosen",
   * and before the profile arrives the honest answer is *nothing* — rendering
   * a checked English row there is a positive claim about a value nobody has
   * seen. `useSettingsActions` keeps the same distinction for the Settings row.
   */
  readonly pickerLocale: string | undefined;
  /** Display label for {@link selectedLanguage} — THE ONE LABEL RULE. */
  readonly selectedLanguageLabel: string;
  /** Available language options */
  readonly supportedLanguages: readonly LocaleOption[];
  /**
   * Sets the per-summary override. Deliberately synchronous and deliberately
   * NOT a profile write: since SE-6511 picking a language changes this summary
   * and nothing else.
   */
  readonly selectLanguage: (language: string) => void;
  /** Explicitly writes the current override to the profile as the default. */
  readonly saveAsDefault: () => void;
  /** The state of that explicit save. */
  readonly saveDefaultState: SaveDefaultLanguageState;
}

/**
 * THE AI-summary language state machine (SE-6506).
 *
 * It used to exist twice — here and again inside `AIPlayerSummaryContainer` —
 * and the two copies had already diverged. Only the container carried the
 * SE-4074 guard that stops a late or stale Apollo `languagePreference` from
 * overwriting the language restored from AsyncStorage when tab navigation
 * remounts the surface, so every consumer of {@link useAISummary}
 * (`PlayerSummaryCard`, `AIScoutingSummaryCard`) still shipped that bug. One
 * implementation means one place to fix it, and one place for [LPW-1.6] to
 * change when the picker becomes a per-summary override.
 *
 * The guard is structural rather than a race. Instead of a profile-sync effect
 * racing a ref latch, the language is **derived**; no effect writes it, so
 * there is nothing to order.
 *
 * ## THE ARBITRATION (SE-6511) — write it here, not only in the ticket
 *
 * Three sources contend. They are resolved in exactly this order:
 *
 * 1. `override` — the language picked on THIS surface, for THIS summary;
 * 2. the profile's raw `languagePreference` — the account default;
 * 3. `restored` — the last-generated language read back from AsyncStorage;
 *
 * and only then {@link FALLBACK_LANGUAGE}, which exists so a generation always
 * has a language, never so a picker has something to tick.
 *
 * SE-6506 ordered these `override → restored → profile`, and SE-6511 inverts
 * the bottom two deliberately. That ordering was correct while picking a
 * language WROTE the profile: the two agreed, and `restored` was the more
 * specific record of what the user was looking at. Now that the picker is a
 * per-summary override, `restored` is merely the language of the last
 * generation — evidence of a one-off choice that has expired, not of what this
 * account wants. Letting it outrank the profile would make the override
 * effectively permanent, which is the opposite of this ticket.
 *
 * `restored` keeps rung 3 rather than being deleted: while the profile is
 * unresolved it is a better answer than English, and it is the whole answer for
 * a viewer with no profile at all. Once the profile arrives, the profile wins —
 * derived, so no effect has to be sequenced to make that true.
 *
 * The profile read on rung 2 is deliberately RAW: `resolveDisplayLocale` would
 * collapse a stranded `ja_JP` into `en_US` here and lose the distinction the
 * picker needs (SE-6510). Resolution happens at the label and picker
 * boundaries, not in the machine.
 * @param playerId - The player the summary belongs to; the restore key
 * @param summaryType - Which summary this surface shows. It namespaces the
 * restore key, so the scouting card's last-generated language cannot steer the
 * player-summary card at a language it has no cached summary for.
 * @returns The language, its label, the offered set, and the override setters
 */
export function useAISummaryLanguage(
  playerId: string | null | undefined,
  summaryType: SummaryType = "player"
): UseAISummaryLanguageReturn {
  /*
   * The failure copy is resolved in the APP's UI locale, which is correct and
   * is deliberately NOT the summary's language: this is the app talking to the
   * user about a write that failed, not the summary. Nothing here may call
   * `setAppLocale` — a per-summary override re-pointing the whole app
   * (dictation included, via SE-6509) is exactly the defect SE-6511 removed.
   */
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const [updateMyProfile] = useAuthUpdateMyProfileMutation();

  const [overrideLanguage, setOverrideLanguage] = useState<string | null>(null);
  const [saveOutcome, setSaveOutcome] = useState<SaveOutcome>("none");

  /*
   * The restored language is stored WITH the target that produced it, never
   * beside it.
   *
   * `loadLastGeneratedLanguage` reads a key scoped by player AND summary type,
   * so the target is a pair. Tracking only the player — which this did, and
   * which both of PR #7340's review threads are one bug about — makes the
   * in-memory state narrower than the read, and that gap has two exits. A read
   * resolving to "nothing saved" leaves the previous target's language sitting
   * in state, and a read for the old target passes a player-only guard and can
   * land after the new one. Either way `useAISummary` then loads the cache
   * under a language the current target has no summary for and clears the
   * summary on screen, which is the failure this ticket was reopened for.
   *
   * Pairing the value with its target closes both. A record for another target
   * cannot be read (the derivation below rejects it) and cannot be written (the
   * ref guard rejects it), so neither the leak nor the race has a path.
   */
  const [restoredRecord, setRestoredRecord] = useState<{
    readonly target: string;
    readonly language: string | null;
  } | null>(null);
  const restoreTargetRef = useRef<string | null>(null);

  const profileLanguage = currentUser?.languagePreference;

  /*
   * Null for a player-less surface (the match report), which has nothing to
   * restore and must not adopt another surface's record.
   */
  const restoreTarget = playerId ? `${summaryType}:${playerId}` : null;

  /*
   * Derived, not stored, so a target change drops the old language on the SAME
   * render rather than waiting for the new read to answer. Without that, the
   * window between the change and the answer still shows the previous target's
   * language, which is long enough for the cache effect to fire on it.
   */
  const restoredLanguage =
    restoredRecord !== null && restoredRecord.target === restoreTarget
      ? restoredRecord.language
      : null;

  /*
   * `undefined` here means NOTHING HAS RESOLVED — no override, no profile, no
   * restored language — which is a different fact from "the answer is English".
   * Only `selectedLanguage` below is allowed to collapse it, because generation
   * needs a code and a picker does not.
   */
  const pickerLocale =
    overrideLanguage ?? profileLanguage ?? restoredLanguage ?? undefined;

  const selectedLanguage = pickerLocale ?? FALLBACK_LANGUAGE;

  /*
   * THE ONE LABEL RULE (SE-6508). This was a `find` over the offered six with
   * a raw-code fallback, so a user stranded on one of the fourteen locales
   * LPW-1.0 dropped read `Generate · ja_JP` while Settings showed "English"
   * for the same stored value. SE-6506 kept the fallback because label and
   * generated output agreed — but they no longer do: since SE-6505 the prompt
   * builders resolve through `getLanguageLabel` too, so a `ja_JP` profile
   * already generates ENGLISH. The raw code had become the lie. One function
   * now names a language everywhere, and it names the language that will
   * actually be produced.
   */
  const selectedLanguageLabel = useMemo(
    () => getLanguageLabel(selectedLanguage),
    [selectedLanguage]
  );

  /*
   * Picking a language used to write `me.languagePreference`, which is the
   * defect SE-6511 exists to remove: choosing French for one summary silently
   * re-pointed the Settings drawer AND the language the speech recognizer
   * listens in (SE-6509 made dictation read the same field). The override is
   * now local to this surface and to this mount.
   *
   * The save-outcome reset lives here, in the event handler, rather than in an
   * effect keyed on the override — `react-hooks/set-state-in-effect` forbids
   * that shape, and there is no need for it: the only thing that invalidates a
   * previous save is a new selection, which is exactly this call.
   */
  const selectLanguage = useCallback((language: string) => {
    setOverrideLanguage(language);
    setSaveOutcome("none");
  }, []);

  /*
   * The ONLY profile write on this surface, and it happens only because the
   * user asked for it by name. A rejected write leaves the override in place:
   * the summary the user is reading is still in the language they picked, and
   * saying otherwise would be the "failed write reported as success" the AC
   * forbids.
   *
   * The override is narrowed to an offered locale before it is written, so this
   * path can never store one of the fourteen dropped locales — and can never
   * quietly rewrite a stranded user's stored preference.
   */
  const saveAsDefault = useCallback(() => {
    if (!isSupportedUiLocale(overrideLanguage)) return;
    const locale = overrideLanguage;
    setSaveOutcome("saving");
    /*
     * `await` inside the try, rather than a `.catch()` on the chain, because
     * the two failures have to land in the same place: a mutate function that
     * throws SYNCHRONOUSLY produces no promise to reject, so a bare `.catch()`
     * would let the exception escape into the press handler and leave the row
     * on "Saving…" — a failed write showing no failure, which is the one thing
     * this ticket says must never happen.
     */
    const write = async () => {
      try {
        const result = await updateMyProfile({
          variables: {
            input: { languagePreference: locale as SupportedLocale },
          },
          // The Settings drawer must read the new default afterwards; the same
          // refetch `useSettingsActions` uses for the same write.
          refetchQueries: ["AuthMe"],
        });
        // A resolved mutation is not automatically a successful one: with a
        // partial-error policy the payload can come back empty.
        setSaveOutcome(result.data?.updateMyProfile ? "saved" : "error");
      } catch {
        setSaveOutcome("error");
      }
    };
    void write();
  }, [overrideLanguage, updateMyProfile]);

  const saveDefaultState = useMemo<SaveDefaultLanguageState>(() => {
    if (overrideLanguage === null) return { status: "unavailable" };
    if (saveOutcome === "saving") return { status: "saving" };
    if (saveOutcome === "saved") return { status: "saved" };
    if (saveOutcome === "error") {
      return { status: "error", message: t(SAVE_DEFAULT_LANGUAGE_ERROR_KEY) };
    }
    /*
     * Only claimable once the profile has RESOLVED. While it is undefined we
     * cannot know whether this override already is the default, and guessing
     * either way is the collapse this ticket is about — so the affordance is
     * offered rather than pre-answered.
     */
    if (profileLanguage !== undefined && profileLanguage === overrideLanguage) {
      return { status: "already-default" };
    }
    return { status: "idle" };
  }, [overrideLanguage, profileLanguage, saveOutcome, t]);

  // Read back the last-generated language so the cached summary that loads
  // after a remount is the one the user was actually looking at (SE-4074).
  useEffect(() => {
    // Tracked so a read for a previous target that resolves after the switch
    // cannot apply its answer to the target now on screen.
    restoreTargetRef.current = restoreTarget;
    if (!playerId || restoreTarget === null) return;
    const restore = async () => {
      const lastLang = await loadLastGeneratedLanguage(playerId, summaryType);
      if (restoreTargetRef.current !== restoreTarget) return;
      /*
       * Recorded even when nothing was saved. Returning early on absence is
       * what let the previous target's language survive into this one — the
       * answer "this target has no language" has to be written down, because
       * it is the answer that clears the carry-over.
       */
      setRestoredRecord({ target: restoreTarget, language: lastLang ?? null });
    };
    restore();
  }, [playerId, summaryType, restoreTarget]);

  return {
    selectedLanguage,
    pickerLocale,
    selectedLanguageLabel,
    supportedLanguages: SUPPORTED_UI_LOCALE_OPTIONS,
    selectLanguage,
    saveAsDefault,
    saveDefaultState,
  };
}

/**
 * Props for useAISummary hook
 */
interface UseAISummaryProps {
  /** Player data fragment */
  player: PlayerDetailPlayerMetaFragment | null | undefined;
  /** Optional Kanban phase context for prompt */
  kanbanPhase?: { phaseName: string; boardName: string } | null;
  /** Truncation limit for display content */
  truncationLimit?: number;
  /** Whether to generate a player summary or scouting summary */
  summaryType?: SummaryType;
  /** Notes data for scouting summary prompt generation */
  notesData?: NotesListNotesByPlayerIdQuery;
}

/**
 * Return type for useAISummary hook
 */
interface UseAISummaryReturn {
  /** Truncated summary content for display */
  displayContent: string;
  /** Full summary content */
  fullContent: string;
  /** Whether summary is generating */
  isGenerating: boolean;
  /** Error message if any */
  error: string | null;
  /** Formatted last updated timestamp */
  lastUpdated: string | null;
  /** Whether to show "Read more" link */
  showReadMore: boolean;
  /** The language a generation will run in. Never null. */
  selectedLanguage: string;
  /** The selection a picker should mark — undefined until something resolves. */
  pickerLocale: string | undefined;
  /** Display label for selected language */
  selectedLanguageLabel: string;
  /** Available language options */
  supportedLanguages: readonly LocaleOption[];
  /** Generate/refresh summary */
  handleGenerate: () => void;
  /** Sets the per-summary language override. Writes no profile. */
  handleLanguageChange: (language: string) => void;
  /** Generate summary in a specific language (changes language + generates) */
  handleGenerateInLanguage: (language: string) => void;
  /** Explicitly promotes the override to the account default. */
  handleSaveLanguageAsDefault: () => void;
  /** The state of that explicit save. */
  saveDefaultState: SaveDefaultLanguageState;
  /** Disconnect SSE connection */
  disconnect: () => void;
}

/**
 * Shared hook for AI summary generation
 *
 * Provides all the logic needed for AI summary components:
 * - SSE generation with streaming
 * - Language selection and profile sync
 * - Caching and cache loading
 * - Error handling
 * - Content truncation
 * @param props - Hook props
 * @param props.player - Player data
 * @param props.kanbanPhase - Optional Kanban context
 * @param props.truncationLimit - Truncation limit (default 300)
 * @param props.summaryType - Whether to generate a player or scouting summary
 * @param props.notesData - Notes query data for scouting summary prompt
 * @returns Summary state and handlers
 */
export function useAISummary({
  player,
  kanbanPhase = null,
  truncationLimit = DEFAULT_TRUNCATION_LIMIT,
  summaryType = "player",
  notesData,
}: UseAISummaryProps): UseAISummaryReturn {
  const cachePrefix =
    summaryType === "scouting" ? SCOUTING_CACHE_KEY_PREFIX : CACHE_KEY_PREFIX;
  // State
  const [summaryContent, setSummaryContent] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Hooks
  const currentUser = useCurrentUser();
  const auth = useAuth();

  /*
   * A cache read goes stale in TIME as well as in identity.
   *
   * The cache effect below already refuses an answer for a target the user has
   * navigated away from. It could not refuse an answer for the target they are
   * still on, which is precisely the read that outlives a generation started
   * while it was in flight — and that read then wipes streamed content on a
   * miss or replaces it with the old summary on a hit.
   *
   * Two refs, because one cannot hold both facts:
   *
   * - `generationEpochRef` rises whenever a generation starts, so a read
   *   issued BEFORE it can tell that the world moved on underneath it;
   * - `generationTargetRef` names the target a LIVE generation is producing,
   *   so a read issued AFTER it — the cache effect re-firing because
   *   "generate in Spanish" changed the language and started generating in one
   *   action — knows the content is already owned. The epoch cannot see that
   *   one: it was captured after the bump, so it still matches.
   *
   * It names a target rather than being a bare "is generating" flag on
   * purpose. A generation that never completes would otherwise freeze the
   * cache effect for every OTHER player the user visits afterwards.
   */
  const generationEpochRef = useRef(0);
  const generationTargetRef = useRef<string | null>(null);

  // The one language state machine — shared with AIPlayerSummaryContainer.
  const {
    selectedLanguage: localSelectedLanguage,
    pickerLocale,
    selectedLanguageLabel,
    supportedLanguages,
    selectLanguage,
    saveAsDefault,
    saveDefaultState,
  } = useAISummaryLanguage(player?.id, summaryType);

  // SSE callbacks
  const handleContentUpdate = useCallback((content: string) => {
    const cleaned = cleanSummaryOutput(content);
    setSummaryContent(cleaned);
  }, []);

  const handleGenerationComplete = useCallback(
    async (finalContent: string, metadata: GenerationMetadata) => {
      const cleaned = cleanSummaryOutput(finalContent);
      setSummaryContent(cleaned);

      const timestamp = await saveSummaryToCache(
        metadata.playerId,
        cleaned,
        metadata.language,
        cachePrefix
      );
      await saveLastGeneratedLanguage(
        metadata.playerId,
        metadata.language,
        summaryType
      );
      // Recorded for this session so a tab-switch remount can restore it even
      // if the profile-vs-restored arbitration lands on a different language
      // afterwards (SE-8594) — see `stores/aiSummarySession`'s own docstring.
      recordSessionGeneratedSummary(
        cachePrefix,
        metadata.playerId,
        cleaned,
        timestamp
      );
      setLastUpdated(timestamp);
      setIsGenerating(false);
      // The generation no longer owns this target; a later cache read for it
      // is legitimate again. The epoch still fences off reads issued earlier.
      generationTargetRef.current = null;
    },
    [cachePrefix, summaryType]
  );

  const handleGenerationError = useCallback((errorMsg: string) => {
    if (!errorMsg) {
      setError(null);
      return;
    }
    setSummaryContent("");
    setError(errorMsg);
    setIsGenerating(false);
    generationTargetRef.current = null;
  }, []);

  // SSE generation hook
  const { generateWithSSE, disconnect } = useSSEGeneration({
    onContentUpdate: handleContentUpdate,
    onComplete: handleGenerationComplete,
    onError: handleGenerationError,
    onGeneratingChange: setIsGenerating,
  });

  // Generate summary
  const generateSummary = useCallback(
    async (forceRefresh = false, languageOverride?: string) => {
      const language = languageOverride || localSelectedLanguage;

      if (!player || !player.id) {
        handleGenerationError(
          "Invalid player data. Please try refreshing the page."
        );
        return;
      }

      const sseEndpoint = env.EXPO_PUBLIC_SSE_ENDPOINT;
      if (!sseEndpoint) {
        handleGenerationError(
          "Service configuration error. Please contact support."
        );
        Sentry.captureMessage("SSE endpoint not configured", {
          level: "error",
          tags: { context: "ai-summary-config-missing" },
        });
        return;
      }

      if (!forceRefresh && isGenerating) {
        return;
      }

      // SE-5426/SE-5427: authenticated-only; skip until the real org resolves.
      if (!currentUser?.organizationId) {
        return;
      }

      // Check cache first
      if (!forceRefresh) {
        const cached = await loadCachedSummary(
          player.id,
          language,
          cachePrefix
        );
        if (cached) {
          setSummaryContent(cached.content);
          setLastUpdated(cached.timestamp);
          return;
        }
      }

      setSummaryContent("");
      setError(null);

      const prompt =
        summaryType === "scouting"
          ? generateScoutingSummaryPrompt(player, notesData, language)
          : generatePlayerPrompt(player, kanbanPhase, language);

      const request = buildPlayerSummaryRequest(
        currentUser,
        prompt,
        player.id,
        auth?.data?.accessToken
      );
      if (!request) {
        return;
      }

      const metadata: GenerationMetadata = {
        language,
        playerId: player.id,
      };

      /*
       * Claimed immediately before dispatch, so every early return above
       * leaves a pending cache read alone: those paths produce no content, and
       * invalidating a read that would have filled the summary is a worse
       * answer than letting it land.
       */
      generationEpochRef.current += 1;
      generationTargetRef.current = `${cachePrefix}|${language}|${player.id}`;

      generateWithSSE(request, metadata);
    },
    [
      player,
      kanbanPhase,
      currentUser,
      generateWithSSE,
      isGenerating,
      auth,
      localSelectedLanguage,
      handleGenerationError,
      summaryType,
      notesData,
      cachePrefix,
    ]
  );

  // Handle generate button
  const handleGenerate = useCallback(() => {
    disconnect();
    setSummaryContent("");
    setError(null);
    generateSummary(true);
  }, [generateSummary, disconnect]);

  // Handle generate in specific language (combined action)
  const handleGenerateInLanguage = useCallback(
    (language: string) => {
      // Set the per-summary override. Nothing is persisted to the profile —
      // "generate this one in French" is a statement about this summary.
      selectLanguage(language);

      // Generate with the selected language
      disconnect();
      setSummaryContent("");
      setError(null);
      generateSummary(true, language);
    },
    [selectLanguage, disconnect, generateSummary]
  );

  // Formatted timestamp
  const formattedLastUpdated = useMemo(
    () => formatLastUpdated(lastUpdated),
    [lastUpdated]
  );

  // Truncated content for display
  const displayContent = useMemo(() => {
    if (!summaryContent) return "";
    if (summaryContent.length <= truncationLimit) return summaryContent;
    return `${summaryContent.slice(0, truncationLimit)}...`;
  }, [summaryContent, truncationLimit]);

  // Whether to show "Read more"
  const showReadMore = useMemo(
    () => summaryContent.length > truncationLimit,
    [summaryContent, truncationLimit]
  );

  /*
   * Load the cached summary for whatever the current target is.
   *
   * The target is the triple (player, language, prefix), and the read is
   * guarded on it for the same reason the restore read is. This effect refires
   * on every part of that triple, and nothing ordered the answers: a read
   * issued for the OLD target could resolve last and, on a miss, call
   * `setSummaryContent("")` over a summary the CURRENT target has. That is the
   * reopened failure's symptom — the summary reading as not generated —
   * reached with no precedence involved, which is why it is fixed here rather
   * than held behind the arbitration decision.
   *
   * A stale answer is dropped outright rather than applied: it is evidence
   * about a target nobody is looking at, so it may neither fill the content nor
   * clear it.
   */
  const cacheTargetRef = useRef<string | null>(null);
  useEffect(() => {
    const target = player?.id
      ? `${cachePrefix}|${localSelectedLanguage}|${player.id}`
      : null;
    cacheTargetRef.current = target;
    const loadCache = async () => {
      if (!player?.id || target === null) return;

      const epoch = generationEpochRef.current;
      const cached = await loadCachedSummary(
        player.id,
        localSelectedLanguage,
        cachePrefix
      );
      // Still the target on screen?
      if (cacheTargetRef.current !== target) return;
      // Has a generation begun since this read was issued?
      if (generationEpochRef.current !== epoch) return;
      // Is one producing this very target right now?
      if (generationTargetRef.current === target) return;
      if (cached) {
        setSummaryContent(cached.content);
        setLastUpdated(cached.timestamp);
        setError(null);
        return;
      }
      // No AsyncStorage entry under the arbitrated language — but this
      // session may have generated one under a DIFFERENT language moments
      // ago (a tab-switch remount, SE-8594). Recovering it here, rather than
      // in the arbitration, is what keeps the profile-outranks-restored
      // decision (SE-6511) intact: the language this effect looked up under,
      // and that a new generation or the picker will use, is unchanged.
      const sessionSummary = resolveSessionGeneratedSummary(
        sessionGeneratedSummariesVar(),
        cachePrefix,
        player.id
      );
      if (sessionSummary) {
        setSummaryContent(sessionSummary.content);
        setLastUpdated(sessionSummary.lastUpdated);
        setError(null);
      } else {
        setSummaryContent("");
        setLastUpdated(null);
        setError(null);
      }
    };
    loadCache();
  }, [player?.id, localSelectedLanguage, cachePrefix]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    displayContent,
    fullContent: summaryContent,
    isGenerating,
    error,
    lastUpdated: formattedLastUpdated,
    showReadMore,
    selectedLanguage: localSelectedLanguage,
    pickerLocale,
    selectedLanguageLabel,
    supportedLanguages,
    handleGenerate,
    handleLanguageChange: selectLanguage,
    handleGenerateInLanguage,
    handleSaveLanguageAsDefault: saveAsDefault,
    saveDefaultState,
    disconnect,
  };
}
